import { Component, OnInit } from '@angular/core';
import { SortType } from 'shared-lib';
import { SearchType } from '../../../models/searchType.model';
import { MvPoleService } from 'shared-lib';
import { ToastrService } from 'ngx-toastr';
import { DateType, MvPoleAppliedFilter, MvPoleDetails, MvPoleFilter, MvPoleStats, MvPoleStatsOptions, UserType } from 'shared-lib';
import { Router } from '@angular/router';
import { ChecklistItem } from '../../../models/checklist-item.model';
import { AuthService } from 'shared-lib';

class MvPoleListState {
  filter: MvPoleFilter;
  appliedFilters: MvPoleAppliedFilter[];
  pageInfo: {
    page: number;
    pageSize: number;
  };

  constructor() {
    this.filter = new MvPoleFilter();
    this.appliedFilters = [];
    this.pageInfo = {
      page: 1,
      pageSize: 10,
    };
  }
}

@Component({
  selector: 'app-mv-pole-list',
  templateUrl: './mv-pole-list.component.html',
  styleUrls: ['./mv-pole-list.component.scss'],
  standalone: false,
})
export class MvPoleListComponent implements OnInit {
  constructor(
    private mvPoleService: MvPoleService,
    private toastr: ToastrService,
    private router: Router,
    private authService: AuthService
  ) {
    this.hasCimDataIntegration = this.authService.hasCimDataIntegration;
  }

  hasCimDataIntegration: boolean = false;
  currentPage: number = 1;
  sortBy: SortType = SortType.NEWEST;
  searchBy: SearchType = SearchType.SLRN;
  pageSize = 10;
  isFilterWindowOpened: boolean = false;
  filter: MvPoleFilter = new MvPoleFilter();
  appliedFilters: MvPoleAppliedFilter[] = [];
  checklistItems: ChecklistItem[] = [];
  stats: MvPoleStats = new MvPoleStats();
  mvPoles: MvPoleDetails[] = [];
  count: number = 0;

  mapSortBy: Record<number, string> = {
    [SortType.NEWEST]: 'Newest',
    [SortType.ID_ASC]: 'ID',
    [SortType.ID_DESC]: 'ID',
    [SortType.SLRN_ASC]: 'SLRN',
    [SortType.SLRN_DESC]: 'SLRN',
  };

  mapFilterLabels: Record<string, string> = {
    'Search': 'COMMON.SEARCH',
    'Filter By': 'COMMON.FILTER_BY',
    'User Type': 'COMMON.USER.TYPE.TITLE',
    'Type': 'COMMON.TYPE',
    'Status': 'COMMON.STATUS',
    'Date From': 'COMMON.DATE_FROM',
    'Date To': 'COMMON.DATE_TO',
    'Date Type': 'COMMON.DATE_TYPE.TITLE',
    'Area': 'COMMON.UT.SINGLE',
    'User': 'COMMON.USER.SINGLE',
  };

  mapFilterValues: Record<string, string> = {
    'First Captured User': 'COMMON.USER.TYPE.FIRST_CAPTURED',
    'Last Captured User': 'COMMON.USER.TYPE.LAST_CAPTURED',
    'Reassigned By': 'COMMON.USER.TYPE.REASSIGNED',
    'Validated By': 'COMMON.USER.TYPE.VALIDATED',
    'Audited By': 'COMMON.USER.TYPE.AUDITED',
    'Concrete': 'COMMON.POLE.TYPE.CONCRETE',
    'Wooden': 'COMMON.POLE.TYPE.WOODEN',
    'Pending': 'COMMON.ASSET_STATUS.PENDING',
    'Complete': 'COMMON.ASSET_STATUS.COMPLETE',
    'Rejected': 'COMMON.ASSET_STATUS.REJECTED',
    'Assigned': 'COMMON.ASSET_STATUS.ASSIGNED',
    'Last Update Time': 'COMMON.DATE_TYPE.LAST_UPDATE',
    'Tagged Date': 'COMMON.DATE_TYPE.TAGGED',
    'Validated Date': 'COMMON.DATE_TYPE.VALIDATED',
  };

  sortByName: string = this.mapSortBy[SortType.NEWEST];

  ngOnInit(): void {
    const cachedFilters = localStorage.getItem('filterMvPoles');
    if (cachedFilters) {
      const { appliedFilters, filter: cachedFilter, pageSize: pageSize, page: page, count: count } = JSON.parse(cachedFilters);
      this.appliedFilters = appliedFilters || [];
      this.filter = new MvPoleFilter();
      this.pageSize = pageSize;
      this.currentPage = page;
      this.count = count;
      if (cachedFilter) {
        Object.assign(this.filter, cachedFilter);
      }
      this.updateChecklistItems();
      localStorage.removeItem('filterMvPoles');
    }
    this.onPageChange(this.currentPage);
    this.getStats();
  }

  onRowClick(mvPoleId: string) {
    const filtersToCache = {
      appliedFilters: this.appliedFilters,
      filter: this.filter,
      pageSize: this.pageSize,
      page: this.currentPage,
      count: this.count
    };
    localStorage.setItem('filterMvPoles', JSON.stringify(filtersToCache));
    this.router.navigate(['asset-list/mv-poles/edit', mvPoleId]);
  }

  private updateChecklistItems() {
    this.checklistItems = this.appliedFilters.map((filter) => {
      const propertyTranslation = this.mapFilterLabels[filter.label] || filter.label;
      const valueTranslation = this.mapFilterValues[filter.value] || filter.value;
      return new ChecklistItem(filter.label, filter.value, propertyTranslation, valueTranslation);
    });
  }

  onFilterWindowChange(val: boolean) {
    this.isFilterWindowOpened = val;
  }

  closeFilterWindow() {
    this.isFilterWindowOpened = false;
  }

  onPageChange(currentPage: number) {
    this.currentPage = currentPage;
    this.reloadTable(currentPage);
  }

  onSortTypeChange(option: SortType) {
    this.sortBy = option;
    this.sortByName = this.mapSortBy[option];
    this.reloadTable();
  }

  onAddFilterCard(data: ChecklistItem) {
    const existingSlrnFilter = this.appliedFilters.find((f) => f.label === 'Search');
    if (existingSlrnFilter) {
      this.appliedFilters = this.appliedFilters.filter((f) => f.label !== 'Search');
    }
    this.appliedFilters.push({ label: data.property, value: data.selectedValue });
    this.filter.search = data.selectedValue; // Ažuriraj search vrednost
    this.updateChecklistItems();
  }

  onAddFilter(filter: MvPoleFilter) {
    const currentSearch = this.filter.search;
    this.filter = filter;
    if (!filter.search) {
      this.filter.search = currentSearch;
    }
    this.updateChecklistItems();
  }

  onAddFilterCards(data: MvPoleAppliedFilter[]) {
    const currentSearchFilter = this.appliedFilters.find(f => f.label === 'Search');
    this.appliedFilters = data;
    // Ako search filter nije u novim podacima, očuvaj ga
    if (currentSearchFilter && !data.some(f => f.label === 'Search')) {
      this.appliedFilters.push(currentSearchFilter);
    }
    this.updateChecklistItems();
  }

  applyFilter() {
    this.getStats();
    this.reloadTable(1);
  }

  removeFilter(checklistItem: ChecklistItem) {
    const filter = this.appliedFilters.find(
      (f) => f.label === checklistItem.property && f.value === checklistItem.selectedValue
    );
    if (filter) {
      this.appliedFilters = this.appliedFilters.filter((f) => f !== filter);
      this.updateFilterFromRemovedItem(filter);
      this.updateChecklistItems();
      this.applyFilter();
    }
  }

  resetFilter() {
    this.filter = new MvPoleFilter();
    this.appliedFilters = [];
    this.checklistItems = [];
    this.sortBy = SortType.NEWEST;
    this.sortByName = this.mapSortBy[SortType.NEWEST];
    this.getStats();
    this.onPageChange(1);
  }

  private updateFilterFromRemovedItem(filter: MvPoleAppliedFilter) {
    if (filter.label === 'Search') {
      this.filter.search = '';
    } else if (filter.label === 'Status') {
      this.filter.status = null;
    } else if (filter.label === 'Type') {
      this.filter.type = 0;
    } else if (filter.label === 'Area') {
      if (this.hasCimDataIntegration) {
        this.filter.selectedSubGeographicalRegions = this.filter.selectedSubGeographicalRegions.filter((id) => id !== filter.id);
      } else {
        this.filter.selectedAreas = this.filter.selectedAreas.filter((area) => area !== filter.id);
      }
    } else if (filter.label === 'User') {
      this.filter.selectedUsers = this.filter.selectedUsers.filter((user) => user !== filter.id);
    } else if (filter.label === 'Date From') {
      this.filter.dateFrom = null;
    } else if (filter.label === 'Date To') {
      this.filter.dateTo = null;
    } else if (filter.label === 'Date Type') {
      this.filter.dateType = DateType.LastUpdateTime;
    } else if (filter.label === 'User Type') {
      this.filter.userType = UserType.None;
      this.filter.selectedUsers = [];
      this.appliedFilters = this.appliedFilters.filter((f) => f.label !== 'User');
      this.updateChecklistItems();
    }
  }

  private getFilterObject() {
    const obj: MvPoleFilter = { ...this.filter };
    obj.search = this.filter.search || '';
    obj.sortBy = this.sortBy;
    return obj;
  }

  getStats() {
    this.getCardStats(MvPoleStatsOptions.Total);
    this.getCardStats(MvPoleStatsOptions.Kv11Feeders);
    this.getCardStats(MvPoleStatsOptions.Retagged);
    this.getCardStats(MvPoleStatsOptions.LvReticulation);
    this.getCardStats(MvPoleStatsOptions.GoodCondition);
  }

  private getCardStats(option: MvPoleStatsOptions) {
    const filter = this.getFilterObject();
    const statsFilter = {
      filter: filter,
      option: option,
    };
    this.mvPoleService.getStats(statsFilter).subscribe({
      next: (response: any) => {
        switch (option) {
          case MvPoleStatsOptions.Total:
            this.stats.total = response?.body?.data?.total ?? 0;
            break;
          case MvPoleStatsOptions.Kv11Feeders:
            this.stats.numberOf11KvFeeders = response?.body?.data?.kV11Feeders ?? 0;
            break;
          case MvPoleStatsOptions.Retagged:
            this.stats.retagged = response?.body?.data?.rettagged ?? 0;
            break;
          case MvPoleStatsOptions.LvReticulation:
            this.stats.lvReticulation = response?.body?.data?.lvReticulation ?? 0;
            break;
          case MvPoleStatsOptions.GoodCondition:
            this.stats.goodCondition = response?.body?.data?.goodCondition ?? 0;
            break;
        }
      },
      error: () => this.toastr.error('Error occurred'),
    });
  }

  reloadTable(page: number | null = null) {
    if (page) this.currentPage = page;
    let obj = {
      pageInfo: {
        page: this.currentPage,
        pageSize: this.pageSize,
        count: this.count
      },
      filterParams: this.getFilterObject(),
    };
    this.getAll(obj);
  }

  private getAll(obj: any) {
    this.mvPoleService.getAll(obj).subscribe({
      next: (response: any) => {
        const responseData = response.body;
        if (responseData?.status === 200 || responseData?.status === 'OK') {
          this.mvPoles = responseData?.data?.data?.length
            ? responseData?.data?.data?.map((el: any) => new MvPoleDetails(el))
            : [];
          this.mvPoles.forEach((x) => {
            x.buildingsSLRN = x.buildingsSLRN?.replace(/,/g, ', ');
          });
          this.count = responseData.data.count ?? this.count;
          if (responseData.message !== '') {
            this.toastr.warning(responseData.message);
          }
        } else {
          this.toastr.error(responseData?.message);
        }
      },
      error: () => this.toastr.error('Error occurred'),
    });
  }

  download() {
    const obj = this.getFilterObject();
    this.mvPoleService.download(obj).subscribe({
      next: (response) => {
        const data = response.body;
        if (data?.status === 'OK' || data?.status === '200') {
          window.location.href = data.data;
        } else if (data?.status === 'Pending' || data?.status === '600') {
          this.toastr.info(data?.message || '');
        } else {
          this.toastr.error(data?.message || 'Request failed');
        }
      },
      error: (error) => this.toastr.error(error),
    });
  }
}